import { useQuery, useMutation } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { clinicianAppointmentSuggestions } from '@actions';
import useAppointmentDetails from '@hooks/appointments/useAppointmentDetails';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import { useSessionAppointmentId } from '@hooks/useSessionAppointmentId';
import { useCallback, useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionFormState } from '@hooks/useSessionFormState';
import { SessionForm } from '@components/Clinicians/session-form';
import { debounce } from 'lodash';
import useApptSuggestions from '@components/Clinicians/Session/hooks/useApptSuggestions';
import { errorModal } from 'vl-common/src/components/Modals';

type MutationResult = {
  type?: string;
  error?: { code?: string; message?: string };
  payload?: unknown;
};

export const useAppointmentRecommendations = () => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const appt_id = useSessionAppointmentId();
  const { appointment } = useSessionAppointment();
  const suggestionKey = useId();

  const { detailsCurrentAppointment, invalidate: invalidateApptDetails } = useAppointmentDetails(appt_id);

  const { suggestionMeta, metaStatus } = useApptSuggestions();

  const generalAdviceMeta = suggestionMeta?.genadv;
  const investigationsMeta = suggestionMeta?.inv;

  const { data: initialValue, status: initialStatus } = useQuery({
    queryKey: ['initialSuggestions', appt_id, suggestionKey],
    enabled: metaStatus === 'success' && !!detailsCurrentAppointment?.genadv && !!detailsCurrentAppointment.inv,
    queryFn: () => ({
      initialInv: detailsCurrentAppointment?.inv.map(({ service_type_code }) => [
        (investigationsMeta &&
          Object.keys(investigationsMeta).find((key) =>
            investigationsMeta[key].find((item) => item.service_type_code === service_type_code)
          )) ||
          '',
        service_type_code
      ]),
      initialGenadv: detailsCurrentAppointment?.genadv.map(({ genadv_code }) => genadv_code)
    })
  });

  const { mutateAsync } = useMutation({
    mutationKey: ['clinicianAppointmentSuggestions'],
    mutationFn: (request: Parameters<typeof clinicianAppointmentSuggestions>[0]) =>
      dispatch(clinicianAppointmentSuggestions(request)) as unknown as Promise<MutationResult>,
    onSuccess: () => invalidateApptDetails()
  });

  const generalAdviceValue = detailsCurrentAppointment?.genadv.map(({ genadv_code }) => ({ genadv_code }));
  const investigationsValue = detailsCurrentAppointment?.inv.map(({ service_type_code }) => ({ service_type_code }));

  const [generalAdvice, setGeneralAdvice] = useSessionFormState('generalAdvice', generalAdviceValue);
  const [investigations, setInvestigations] = useSessionFormState('investigations', investigationsValue);

  const onChangeGeneralAdvice = useMemo(
    () =>
      debounce((values: Required<SessionForm>['generalAdvice']) => {
        setGeneralAdvice(values);
        mutateAsync({
          user_guid: String(appointment?.clinician_user_guid),
          appt_id,
          genadv: values,
          inv: investigations || []
        });
      }, 1000),
    [appointment?.clinician_user_guid, appt_id, investigations, mutateAsync]
  );

  const debouncedSaveInvestigations = useMemo(
    () =>
      debounce(
        async (
          values: Required<SessionForm>['investigations'],
          previousInvestigations: Required<SessionForm>['investigations'] | undefined
        ) => {
          const response = await mutateAsync({
            user_guid: String(appointment?.clinician_user_guid),
            appt_id,
            genadv: generalAdvice || [],
            inv: values
          });
          if (response?.error) {
            setInvestigations(previousInvestigations);
            errorModal({
              title: t('Failed to save lab tests'),
              content: response.error?.message || t('Something went wrong. Please try again.')
            });
          }
        },
        1000
      ),
    [appointment?.clinician_user_guid, appt_id, generalAdvice, mutateAsync]
  );

  const onChangeInvestigations = useCallback(
    (values: Required<SessionForm>['investigations']) => {
      const previousInvestigations = investigations;
      setInvestigations(values);
      debouncedSaveInvestigations(values, previousInvestigations);
    },
    [investigations, debouncedSaveInvestigations]
  );

  const investigationsCascaderValue = useMemo(
    () =>
      investigations?.map(({ service_type_code }) => [
        Object.keys(investigationsMeta || {}).find((key) =>
          investigationsMeta?.[key]?.find((item) => item.service_type_code === service_type_code)
        ) || '',
        service_type_code
      ]),
    [investigations, investigationsMeta]
  );

  return {
    defaultGeneralAdviceValue: initialValue?.initialGenadv,
    defaultInvestigationsValue: initialValue?.initialInv,
    investigationsCascaderValue,
    readOnly: appointment?.appointment_completed || appointment?.read_only,
    investigationsMeta: initialStatus === 'success' ? investigationsMeta : null,
    generalAdviceMeta: initialStatus === 'success' ? generalAdviceMeta : null,
    onChangeGeneralAdvice,
    onChangeInvestigations
  };
};
